import { observer } from 'mobx-react';
import { Button, Tooltip } from 'antd';
import { QuestionCircleOutlined, BulbOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';

interface HelpHotspotProps {
  topic: string;
  // Display variant. "icon" = bare (?) circle (default). "label" = "(?) How does this work?"
  // "inline" = small sized for use next to a field label.
  variant?: 'icon' | 'label' | 'inline';
  label?: string;
  size?: 'small' | 'middle';
}

// Dropped anywhere in the app to open the help drawer for a specific topic.
// Keeps placement and styling consistent so users learn to look for it.

export const HelpHotspot = observer(function HelpHotspot({
  topic,
  variant = 'icon',
  label,
  size = 'small',
}: HelpHotspotProps) {
  const { helpStore, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  function handleClick(): void {
    trackEvent('Help.Opened', { topic });
    helpStore.open(topic);
  }

  if (variant === 'label') {
    return (
      <Button
        type="text"
        size={size}
        icon={<BulbOutlined />}
        onClick={handleClick}
        style={{ color: isDark ? colors.slate[300] : colors.slate[500] }}
      >
        {label ?? 'How does this work?'}
      </Button>
    );
  }

  if (variant === 'inline') {
    return (
      <Tooltip title={label ?? 'Learn more'}>
        <button
          type="button"
          onClick={handleClick}
          aria-label={label ?? 'Help'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: isDark ? colors.slate[400] : colors.slate[400],
            cursor: 'pointer',
            verticalAlign: 'middle',
            marginLeft: 4,
          }}
        >
          <QuestionCircleOutlined style={{ fontSize: 13 }} />
        </button>
      </Tooltip>
    );
  }

  // Default: icon-only round hotspot, sized to sit next to a page title.
  return (
    <Tooltip title={label ?? 'Learn how this works'}>
      <button
        type="button"
        onClick={handleClick}
        aria-label={label ?? 'Help'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          padding: 0,
          borderRadius: 999,
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          background: isDark ? colors.slate[800] : '#FFFFFF',
          color: isDark ? colors.slate[300] : colors.slate[500],
          cursor: 'pointer',
          transition: 'border-color 120ms ease, color 120ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.ink[500];
          e.currentTarget.style.color = colors.ink[600];
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = isDark ? colors.slate[700] : colors.slate[200];
          e.currentTarget.style.color = isDark ? colors.slate[300] : colors.slate[500];
        }}
      >
        <QuestionCircleOutlined style={{ fontSize: 13 }} />
      </button>
    </Tooltip>
  );
});
